import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  FaCheck, FaEye, FaBullseye, FaIndustry, FaLeaf,
  FaShieldAlt, FaHandshake, FaAward,
  FaStar, FaHeart, FaUsers, FaLightbulb, FaGlobe, FaTrophy
} from 'react-icons/fa';
import './About.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: 'easeOut' } }
};
const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};
const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const About = () => {
  const { t } = useTranslation();

  const coreValues = [
    { icon: <FaShieldAlt />, title: t('valIntegrity'), desc: t('valIntegrityDesc'), color: '#04416d' },
    { icon: <FaLightbulb />, title: t('valInnovation'), desc: t('valInnovationDesc'), color: '#e33355' },
    { icon: <FaHeart />, title: t('valCustomerFirst'), desc: t('valCustomerFirstDesc'), color: '#0b5c96' },
    { icon: <FaLeaf />, title: t('valQuality'), desc: t('valQualityDesc'), color: '#2d8a4e' },
    { icon: <FaUsers />, title: t('valExpertise'), desc: t('valExpertiseDesc'), color: '#7c3aed' },
    { icon: <FaGlobe />, title: t('valGrowth'), desc: t('valGrowthDesc'), color: '#d4294a' },
  ];

  return (
    <div className="about-page">

      {/* ---- PAGE HERO ---- */}
      <section className="about-hero">
        <div className="about-hero-particles">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className={`ap-particle ap-particle-${i + 1}`}
              animate={{ y: [0, -30, 0], rotate: [0, i % 2 === 0 ? 45 : -45, 0], opacity: [0.4, 0.9, 0.4] }}
              transition={{ duration: 5 + i, repeat: Infinity, delay: i * 0.7 }}
            />
          ))}
        </div>
        <div className="about-container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="about-badge">{t('aboutHeroBadge')}</span>
            <h1>{t('aboutHeroTitle1')} <span className="about-highlight">{t('aboutHeroTitle2')}</span></h1>
            <p>{t('aboutHeroDesc')}</p>
          </motion.div>
        </div>
      </section>

      {/* ---- COMPANY PROFILE ---- */}
      <section className="section">
        <div className="about-container">
          <div className="about-grid">
            <motion.div
              className="about-text"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <span className="section-tag-about">{t('aboutStoryTag')}</span>
              <h2 className="about-section-title">{t('aboutStoryTitle1')} <span>{t('aboutStoryTitle2')}</span></h2>
              <p className="lead-text">
                {t('aboutStoryLead')}
              </p>
              <p>
                {t('aboutStoryP2')}
              </p>
              <p>
                {t('aboutStoryP3')}
              </p>
              <ul className="check-list">
                <li><FaCheck className="check-icon" /> {t('aboutCheck1')}</li>
                <li><FaCheck className="check-icon" /> {t('aboutCheck2')}</li>
                <li><FaCheck className="check-icon" /> {t('aboutCheck3')}</li>
                <li><FaCheck className="check-icon" /> {t('aboutCheck4')}</li>
              </ul>
            </motion.div>
            <motion.div
              className="about-image-stack"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img
                src="/Images/image6.png"
                alt="Manufacturing Facility"
                className="img-main"
              />
              <div className="about-img-badge">
                <FaTrophy />
                <div>
                  <strong>{t('aboutEst')}</strong>
                  <span>{t('aboutLegacy')}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- FOUNDER SECTION ---- */}
      <section className="founder-section">
        <div className="founder-glow" />
        <div className="about-container" style={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            className="founder-grid"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="founder-img-wrap">
              <div className="founder-image-card">
                <img
                  src="/Images/leadership.jpg"
                  alt="Digvijay Industries Manufacturing & Leadership"
                  className="founder-img"
                />
              </div>
              <div className="founder-badge">
                <div className="founder-badge-stars">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <p>{t('aboutFounderBadge')}</p>
              </div>
            </div>
            <div className="founder-text">
              <span className="section-tag-about light">{t('aboutFounderTag')}</span>
              <h2 className="about-section-title white">{t('aboutFounderTitle')}</h2>
              <p className="founder-quote">
                {t('aboutFounderQuote')}
              </p>
              <p className="founder-bio">
                {t('aboutFounderBio')}
              </p>
              <div className="founder-achievements">
                <div className="achievement-chip"><FaTrophy /> {t('aboutAchieve1')}</div>
                <div className="achievement-chip"><FaGlobe /> {t('aboutAchieve2')}</div>
                <div className="achievement-chip"><FaAward /> {t('aboutAchieve3')}</div>
                <div className="achievement-chip"><FaUsers /> {t('aboutAchieve4')}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---- SECTION 1: VISION & MISSION ---- */}
      <section className="section vision-mission-section">
        <div className="about-container">
          <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="section-header-centered">
            <span className="section-tag-about">{t('aboutVmTag')}</span>
            <h2 className="about-section-title centered">{t('aboutVmTitle')}</h2>
          </motion.div>

          {/* Vision & Mission Cards */}
          <div className="vm-grid">
            <motion.div
              className="vm-card vision-card"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -8, boxShadow: '0 20px 50px rgba(4,65,109,0.15)' }}
            >
              <div className="vm-icon-wrap primary">
                <FaEye />
              </div>
              <h3>{t('aboutVisionTitle')}</h3>
              <p>{t('aboutVisionDesc')}</p>
              <ul className="vm-list">
                <li><FaCheck /> {t('aboutVision1')}</li>
                <li><FaCheck /> {t('aboutVision2')}</li>
                <li><FaCheck /> {t('aboutVision3')}</li>
                <li><FaCheck /> {t('aboutVision4')}</li>
              </ul>
            </motion.div>

            <motion.div
              className="vm-card mission-card"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              whileHover={{ y: -8, boxShadow: '0 20px 50px rgba(227,51,85,0.15)' }}
            >
              <div className="vm-icon-wrap secondary">
                <FaBullseye />
              </div>
              <h3>{t('aboutMissionTitle')}</h3>
              <p>{t('aboutMissionDesc')}</p>
              <ul className="vm-list">
                <li><FaCheck /> {t('aboutMission1')}</li>
                <li><FaCheck /> {t('aboutMission2')}</li>
                <li><FaCheck /> {t('aboutMission3')}</li>
                <li><FaCheck /> {t('aboutMission4')}</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---- SECTION 2: CORE VALUES ---- */}
      <section className="section core-values-section bg-light">
        <div className="about-container">
          <motion.div
            className="section-header-centered"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-tag-about">{t('aboutValuesTag')}</span>
            <h2 className="about-section-title centered">{t('aboutValuesTitle')}</h2>
          </motion.div>

          <div className="values-grid">
            {coreValues.map((v, i) => (
              <motion.div
                key={i}
                className="value-card"
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -8 }}
                style={{ '--vc': v.color }}
              >
                <div className="value-icon" style={{ background: `${v.color}15`, color: v.color }}>{v.icon}</div>
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- INFRASTRUCTURE ---- */}
      <section className="section">
        <div className="about-container">
          <div className="infra-grid">
            <motion.div
              className="infra-text"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <span className="section-tag-about">{t('aboutInfraTag')}</span>
              <h2 className="about-section-title">{t('aboutInfraTitle1')} <span>{t('aboutInfraTitle2')}</span></h2>
              <p>
                {t('aboutInfraP1')}
              </p>
              <p>
                {t('aboutInfraP2')}
              </p>
              <div className="infra-stats">
                <div className="infra-stat">
                  <FaIndustry className="infra-icon" />
                  <h4>{t('aboutInfraStat1Title')}</h4>
                  <span>{t('aboutInfraStat1Sub')}</span>
                </div>
                <div className="infra-stat">
                  <FaShieldAlt className="infra-icon" />
                  <h4>{t('aboutInfraStat2Title')}</h4>
                  <span>{t('aboutInfraStat2Sub')}</span>
                </div>
                <div className="infra-stat">
                  <FaHandshake className="infra-icon" />
                  <h4>{t('aboutInfraStat3Title')}</h4>
                  <span>{t('aboutInfraStat3Sub')}</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="infra-image"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img
                src="/Images/image7.png"
                alt="Modern Infrastructure"
              />
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
